import { Injectable, OnDestroy } from '@angular/core';
import { UserState, UserType } from '../store/user.state';
import { Subject, takeUntil } from 'rxjs';
import mixpanel, { Dict } from 'mixpanel-browser';
import { UserLocationState } from '../store/user-location.state';
import { EventType } from './analytics/analytics.model';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class MixpanelService implements OnDestroy {
  constructor(
    private userState: UserState,
    private userLocationState: UserLocationState,
  ) {}

  private destroyed$ = new Subject<void>();

  initialize(): void {
    // Init Mixpanel
    mixpanel.init(environment.mixpanelToken, {
      debug: false,
      track_pageview: false,
      persistence: 'localStorage',
    });

    this.userState.user$.pipe(takeUntil(this.destroyed$)).subscribe(user => {
      if (user.userType !== UserType.GUEST) {
        void this.identify(user.id);
      }
    });
  }

  async identify(userId: string): Promise<void> {
    try {
      const userLocation = this.userLocationState.userLocation$.value;
      setTimeout(() => {
        mixpanel.identify(userId);
        mixpanel.track('UserIdentified', { user_id: userId });
        mixpanel.people.set({
          $country_code: userLocation.countryCode,
          $region: userLocation.regionName,
          $city: userLocation.city,
        });
      }, 0); // Making sure that the function doesn't block the initialization of the app
    } catch (error) {
      console.error('Failed to identify user in Mixpanel:', error);
    }
  }

  track<T extends EventType>(eventType: T, attributes: Dict): void {
    mixpanel.track(eventType, attributes);
  }

  reset(): void {
    mixpanel.reset(); // Reset the user in MixPanel
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
